import { NoteEvent } from "@opendaw/lib-dsp";
import { Arrays, UUID } from "@opendaw/lib-std";
import { NoteEventBox } from "@opendaw/studio-boxes";
import { Propagation } from "@opendaw/lib-box";
import { NoteEventCollectionBoxAdapter } from "../collection/NoteEventCollectionBoxAdapter";
export class NoteEventBoxAdapter {
    type = "note-event";
    #context;
    #box;
    #subscription;
    #isSelected = false;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#subscription = this.#box.subscribe(Propagation.Children, (update) => {
            if (this.collection.isEmpty()) {
                return;
            }
            if (update.type === "primitive" || update.type === "pointer") {
                const collection = this.collection.unwrap();
                const updatedFieldKeys = update.address.fieldKeys;
                const pitchChanged = Arrays.equals(this.#box.pitch.address.fieldKeys, updatedFieldKeys);
                const positionChanged = Arrays.equals(this.#box.position.address.fieldKeys, updatedFieldKeys);
                if (pitchChanged || positionChanged) {
                    collection.requestSorting();
                }
                else {
                    collection.onEventPropertyChanged();
                }
            }
        });
    }
    onSelected() {
        this.#isSelected = true;
        this.collection.ifSome(region => region.onEventPropertyChanged());
    }
    onDeselected() {
        this.#isSelected = false;
        this.collection.ifSome(region => region.onEventPropertyChanged());
    }
    terminate() { this.#subscription.terminate(); }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get position() { return this.#box.position.getValue(); }
    get duration() { return this.#box.duration.getValue(); }
    get complete() { return this.position + this.duration; }
    get velocity() { return this.#box.velocity.getValue(); } // 0.0...1.0
    get pitch() { return this.#box.pitch.getValue(); } // 0...127
    get cent() { return this.#box.cent.getValue(); } // -50.0...+50.0
    get chance() { return this.#box.chance.getValue(); } // 0...100%
    get playCount() { return this.#box.playCount.getValue(); } // 1...16
    get playCurve() { return this.#box.playCurve.getValue(); } // -1...+1
    get isSelected() { return this.#isSelected; }
    get collection() {
        return this.#box.events.targetVertex
            .map(vertex => this.#context.boxAdapters.adapterFor(vertex.box, NoteEventCollectionBoxAdapter));
    }
    normalizedPitch() {
        if (this.collection.isEmpty()) {
            return 0.5;
        }
        const { minPitch, maxPitch } = this.collection.unwrap();
        return minPitch === maxPitch ? 0.5 : 1.0 - (this.pitch - minPitch) / (maxPitch - minPitch);
    }
    copyAsNoteEvent() {
        return {
            type: "note-event",
            position: this.position,
            duration: this.duration,
            pitch: this.pitch,
            cent: this.cent,
            velocity: this.velocity
        };
    }
    copyTo(options) {
        return this.#context.boxAdapters.adapterFor(NoteEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(options?.position ?? this.position);
            box.duration.setValue(options?.duration ?? this.duration);
            box.pitch.setValue(options?.pitch ?? this.pitch);
            box.playCount.setValue(options?.playCount ?? this.playCount);
            box.events.refer(options?.events ?? this.collection.unwrap().box.events);
            box.velocity.setValue(this.velocity);
            box.cent.setValue(this.cent);
            box.chance.setValue(this.chance);
        }), NoteEventBoxAdapter);
    }
    computeCurveValue(ratio) { return NoteEvent.curveFunc(ratio, this.playCurve); }
    canConsolidate() { return this.playCount > 1; }
    consolidate() {
        const playCount = this.playCount;
        const events = this.collection.unwrap().box.events;
        const adapters = Arrays.create((index) => {
            const a = Math.floor(this.computeCurveValue(index / playCount) * this.duration);
            const b = Math.floor(this.computeCurveValue((index + 1) / playCount) * this.duration);
            return this.copyTo({
                position: Math.floor(this.position + a),
                duration: Math.max(1, b - a),
                playCount: 1,
                events
            });
        }, playCount);
        this.#box.delete();
        return adapters;
    }
}
